import React, { useMemo } from 'react';
import WidgetRenderer from '../../components/WidgetRenderer';
import { buildPreviewData } from '../../utils/widgetPreviewData';
import { getWidgetSql } from '../../utils/buildJoinSql';
import { shouldSkipWidgetDataFetch } from '../../widgets/registry';
import { isDashboardDataSource } from '../../utils/dashboardData';
import { widgetUsesReportQueries, fieldsForWidget, normalizeReportQueries } from '../../utils/reportQueries';
import { keysFromKpiCard, normalizeKpiCard } from '../../utils/kpiItems';

/**
 * Live designer body: sample rows/columns from the widget query, themed chrome
 * applied by the parent card. Pointer events disabled so drag/resize still work.
 */
export default function DesignerWidgetPreview({ widget, reportQueries = [] }) {
  const extraKeys = React.useMemo(() => {
    const fromQueries = fieldsForWidget(widget, normalizeReportQueries(reportQueries)).map((f) => f.key);
    const allowed = new Set(fromQueries);
    const keep = (keys) => (keys || []).filter((k) => k && allowed.has(k));
    if (widget?.type === 'stat_card') {
      return [...fromQueries, ...keep(keysFromKpiCard(normalizeKpiCard(widget.statConfig || {})))];
    }
    const g = widget?.gaugeConfig || widget?.progressConfig || {};
    return [...fromQueries, ...keep([g.actualKey, g.targetKey, g.valueKey, g.maxKey])];
  }, [widget, reportQueries]);
  const previewData = useMemo(() => buildPreviewData(widget, undefined, extraKeys), [widget, extraKeys]);
  const hasQuery = !!getWidgetSql(widget)
    || shouldSkipWidgetDataFetch(widget?.type)
    || isDashboardDataSource(widget)
    || widgetUsesReportQueries(widget)
    || extraKeys.length > 0;
  const hasColumns = (previewData.columns || []).length > 0;

  if (!hasQuery) {
    return (
      <div className="flex h-full items-center justify-center px-3 text-center">
        <p className="text-[11px] leading-relaxed text-slate-400">
          کوئری را در پنل ویجت تنظیم کنید
          <span className="block text-[10px] mt-0.5">پیش‌نمایش ستون‌ها و رنگ‌ها اینجا دیده می‌شود</span>
        </p>
      </div>
    );
  }

  if (!hasColumns && !shouldSkipWidgetDataFetch(widget?.type) && !isDashboardDataSource(widget) && !widgetUsesReportQueries(widget)) {
    return (
      <div className="flex h-full items-center justify-center px-3 text-center">
        <p className="text-[11px] text-slate-400">ستون‌های کوئری خوانده نشد — SQL را اعمال کنید</p>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full pointer-events-none">
      <WidgetRenderer
        widget={widget}
        preview
        previewData={previewData}
        chrome
      />
      <span className="absolute left-1.5 bottom-1 rounded bg-slate-900/60 px-1.5 py-0.5 text-[8px] font-bold text-white/90 pointer-events-none">
        پیش‌نمایش
      </span>
    </div>
  );
}
